import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useState } from "react"

import CampaignLinksToggle from "@components/dialogs/campaign_links_toggle"
import TextInput from "@components/text_input"
import { useCampaignLinksPageContext } from "@contexts/main/campaign_links_page_context"
import useControlledForm from "@hooks/use_controlled_form"
import useMe from "@hooks/use_me"
import { type CampaignLinksToggleTarget } from "@models/campaign_links"
import { RoleTypes } from "__generated__/graphql"

import LookupResult from "./sub_components/lookup_result"

type Toggle = { target: CampaignLinksToggleTarget; disable: boolean }
type LookupFormData = { url: string }

export default function CampaignLinksPage() {
  const { translate, search, submittedUrl, result, isLoading } =
    useCampaignLinksPageContext()
  const { me } = useMe()
  const [toggle, setToggle] = useState<Toggle | null>(null)
  const { control, handleSubmit, watch } = useControlledForm<LookupFormData>({
    defaultValues: { url: "" }
  })

  // Cosmetic only — the API enforces the ADMIN role on the write.
  const canEdit = me?.role === RoleTypes.Admin

  return (
    <Stack gap={2} sx={{ p: 2, width: "100%", maxWidth: 1100 }}>
      <Typography variant="h5">{translate("campaign_links_title")}</Typography>
      <Alert severity="info">{translate("campaign_links_page_hint")}</Alert>

      <Stack
        component="form"
        direction="row"
        gap={2}
        alignItems="flex-start"
        onSubmit={handleSubmit((data) => search(data.url))}
      >
        <TextInput<LookupFormData>
          name="url"
          control={control}
          label={translate("campaign_links_lookup_label")}
          placeholder="https://link.qpien.com/r/…"
          fullWidth
        />
        <Button
          type="submit"
          variant="contained"
          disabled={!watch("url")?.trim() || isLoading}
          sx={{ mt: 0.5 }}
        >
          {translate("campaign_links_lookup")}
        </Button>
      </Stack>

      {isLoading && <CircularProgress size={24} />}

      {!isLoading && !!submittedUrl && !!result && (
        <LookupResult
          result={result}
          canEdit={canEdit}
          onToggle={(target, disable) => setToggle({ target, disable })}
        />
      )}

      {!!toggle && (
        <CampaignLinksToggle
          isOpen={!!toggle}
          setClose={() => setToggle(null)}
          target={toggle.target}
          disable={toggle.disable}
        />
      )}
    </Stack>
  )
}
